import React, { useState } from "react";
import {
  Box,
  Button,
  ButtonDropdown,
  Header,
  Link,
  SpaceBetween,
} from "@amzn/awsui-components-react-v3";
import Skeleton from "@amzn/awsui-components-console/skeleton";
import { UserStatus } from "../services/idp/idp.types";
import { BadgePhoto } from "./badge-photo";
import { PhoneToolUserUrl } from "../lib/constants";
import { SendRegistrationCode } from "./send-registration-code";
import { formatRoute } from "../utils/route";

// a subset of the User type with only the necessary fields for this component
export interface ProfileUser {
  displayName?: string;
  alias?: string;
  id?: string;
  status?: string;
}

interface UserProfileHeaderProps {
  user?: ProfileUser;
  showActionsDropdown: boolean;
  allowRefresh?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
  showSkeleton?: boolean;
}

const UserProfileHeaderSkeleton = () => (
  <Header variant="h1" data-testid="user-profile-header-skeleton">
    <SpaceBetween direction="horizontal" alignItems="center" size="m">
      <Skeleton variant="text-body-m" height="8rem" width="6rem" />
      <SpaceBetween direction="vertical" size="m">
        <Skeleton variant="text-heading-xl" width="12rem" />
        <Skeleton variant="text-body-m" width="4rem" />
        <Skeleton variant="text-body-s" width="18rem" />
      </SpaceBetween>
    </SpaceBetween>
  </Header>
);

export const UserProfileHeader = ({
  user,
  showActionsDropdown,
  allowRefresh = false,
  onRefresh,
  refreshing = false,
  showSkeleton = false,
}: UserProfileHeaderProps) => {
  const [showSendRegModal, setShowSendRegModal] = useState(false);

  if (showSkeleton || !user?.alias) {
    return <UserProfileHeaderSkeleton />;
  }

  return (
    <>
      <Header
        variant="h1"
        actions={
          (allowRefresh || showActionsDropdown) && (
            <SpaceBetween direction="horizontal" size="xs">
              {allowRefresh && (
                <Button
                  iconName="refresh"
                  onClick={onRefresh}
                  loading={refreshing}
                  data-testid="refresh-user-button"
                />
              )}
              {showActionsDropdown && (
                <ButtonDropdown
                  items={[
                    {
                      id: "send-registration-message",
                      text: "Send registration code",
                      disabled: user.status === UserStatus.Disabled,
                      disabledReason:
                        "Registration codes cannot be sent to disabled users.",
                    },
                  ]}
                  onItemClick={({ detail }) => {
                    if (detail.id === "send-registration-message") {
                      setShowSendRegModal(true);
                    }
                  }}
                  data-testid="user-actions-dropdown"
                >
                  Actions
                </ButtonDropdown>
              )}
            </SpaceBetween>
          )
        }
      >
        <SpaceBetween direction="horizontal" alignItems="center" size="m">
          <BadgePhoto alias={user.alias} size="8rem" />
          <SpaceBetween direction="vertical" size="xxxs">
            <Box variant="h1" color="inherit">
              {user.displayName}
            </Box>
            <Link
              href={formatRoute(PhoneToolUserUrl, {
                routeParams: { alias: user.alias ?? "" },
              })}
              external
              variant="primary"
            >
              {user.alias}
            </Link>
            {user.id && (
              <Box variant="small" color="text-body-secondary">
                KILM ID: {user.id}
              </Box>
            )}
          </SpaceBetween>
        </SpaceBetween>
      </Header>
      {showSendRegModal && (
        <SendRegistrationCode
          alias={user.alias}
          visible={showSendRegModal}
          onDismiss={() => setShowSendRegModal(false)}
        />
      )}
    </>
  );
};
